import {AttachmentFile, RawAttachmentFile} from "ts/api/attachmentFile";
import {convertFileUrl} from "tools/convertFileUrl";
import downloadBlobFile from "tools/file/downloadBlobFile";
import isPreviewableFile from "tools/file/isPreviewableFile";

const handleAttachmentPreview = (
  attachment: AttachmentFile | RawAttachmentFile,
  objectUrls: Record<string, string>
) => {
  const {url, file, title} = attachment;
  // Both AttachmentFile (`{ name, size }`) and RawAttachmentFile (`File`) carry
  // a `name` with the extension — prefer it over the (extension-less) title.
  const fileName = (file as {name?: string})?.name || title;

  // Local blob previews (uploaded-but-not-saved files) — open directly.
  const existingUrl = objectUrls[attachment.id];
  if (existingUrl) {
    window.open(existingUrl, "_blank");
    return;
  }
  if (file instanceof File) {
    // Downloadable types (xlsx, docx, ...) would just trigger a download and
    // leave a blank tab — download the local blob instead.
    if (!isPreviewableFile(file.name)) {
      downloadBlobFile(file, file.name).catch(() => undefined);
      return;
    }
    const blobUrl = URL.createObjectURL(file);
    objectUrls[attachment.id] = blobUrl;
    window.open(blobUrl, "_blank");
    return;
  }
  if (!url) return;

  // Downloadable type (xlsx, docx, zip, …): do NOT open a tab. Opening a blank
  // `_blank` tab synchronously and then `close()`-ing it once we know it's a
  // download flashes a white page for a frame. `<a download>` works without a
  // pre-opened tab, so resolve the token URL, fetch and download — no flicker.
  if (!isPreviewableFile(fileName)) {
    convertFileUrl(url, attachment.provider)
      .then(async (finalUrl) => {
        const blob = await (await fetch(finalUrl)).blob();
        await downloadBlobFile(blob, fileName || "download");
      })
      .catch(() => undefined);
    return;
  }

  // Previewable (pdf / image): `convertFileUrl` is async (mints an auth token),
  // so open the tab synchronously to preserve the user gesture and avoid the
  // popup blocker, then redirect once the URL resolves. Do NOT pass `noopener`
  // — that makes `window.open` return `null`, leaving the tab on about:blank.
  const popup = window.open("", "_blank");
  convertFileUrl(url, attachment.provider)
    .then((finalUrl) => {
      if (popup) popup.location.href = finalUrl;
    })
    .catch(() => {
      if (popup) popup.location.href = url;
    });
};

export default handleAttachmentPreview;
